import {
  AdminLayoutComponent,
  AuthLayoutComponent,
  HeaderComponent,
  LayoutComponent,
  MenuComponent,
  OptionsComponent,
  SidebarComponent,
} from "./core";

import { HttpClient, HttpClientModule, HTTP_INTERCEPTORS } from "@angular/common/http";

import { BidiModule } from "@angular/cdk/bidi";
import { BrowserModule } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { AppComponent } from "./app.component";
import { AppRoutes } from "./app.routing";

import { ClipboardModule } from "ngx-clipboard";
import { StoreModule } from "@ngrx/store";

import { EffectsModule } from "@ngrx/effects";
import { planReducer } from "../app/core/store/plan/plan.reducer";
import { PlanEffects } from "../app/core/store/plan/plan.effects";
import { HighlighterPipe } from "./core/core-services/highlighter/highlighter.pipe";

import { MarkdownModule } from "ngx-markdown";
import { NgModule } from "@angular/core";
import { RouterModule, RouteReuseStrategy } from "@angular/router";
import { NgxUiLoaderConfig, NgxUiLoaderModule, POSITION, SPINNER } from "ngx-ui-loader";
import { NgxWebstorageModule } from "ngx-webstorage";
import { CoreModule } from "./core/core-services/core.module";

import { EditorModule } from "@tinymce/tinymce-angular";

import { AuthGuardService } from "./core/core-services/services/auth-guard.service";
import { GuestGuardService } from "./core/core-services/services/guest-guard.service";

import { ProfileResolver } from "./vengreso/profile/profile.resolver";
import { SettingsComponent } from "./vengreso/settings/settings.component";
import { SettingsResolver } from "./vengreso/settings/settings.resolver";
import { EmulatorPreviewComponent } from "./vengreso/shortcuts/actions/emulator-preview/emulator-preview.component";
import { StatisticsComponent } from "./vengreso/shortcuts/actions/statistics/statistics.component";
import { CategoryDependencyComponent } from "./vengreso/shortcuts/add-shortcut-category/category-dependency/category-dependency.component";
import { AddShortcutComponent } from "./vengreso/shortcuts/add-shortcut/add-shortcut.component";
import { DuplicateShortcutComponent } from "./vengreso/shortcuts/duplicate-shortcut/duplicate-shortcut.component";
import { ShortcutCategoryListResolver } from "./vengreso/shortcuts/shortcut-category-list.resolver";
import { ShortcutListResolver } from "./vengreso/shortcuts/shortcut-list.resolver";
import { DeleteVersionComponent } from "./vengreso/shortcuts/version-history/delete-version/delete-version.component";
import { ConfirmVersionComponent } from "./vengreso/shortcuts/version-history/preview/confirm-version-dialog/confirm-version-dialog.component";
import { PreviewComponent } from "./vengreso/shortcuts/version-history/preview/preview.component";
import { VersionHistoryComponent } from "./vengreso/shortcuts/version-history/version-history.component";
import { TemplateCategoryCopyComponent } from "./vengreso/templates/template-category-copy/template-category-copy.component";
import { TemplateCopyComponent } from "./vengreso/templates/template-copy/template-copy.component";
import { TemplatesCategoryListResolver } from "./vengreso/templates/templates-category-list.resolver";
import { TemplatesResolver } from "./vengreso/templates/templates.resolver";
import { ProfileComponent } from "./vengreso/profile/profile.component";
import { CategoryRenameComponent } from "./vengreso/shortcuts/add-shortcut-category/category-rename/category-rename.component";
import { ChangeShortcutCategoryComponent } from "./vengreso/shortcuts/change-shortcut-category/change-shortcut-category.component";

import { DragDropModule } from "@angular/cdk/drag-drop";
import { GoogleLoginProvider, SocialAuthServiceConfig } from "@abacritt/angularx-social-login";
import { environment } from "src/environments/environment";

import { AdvancedSearchDialogComponent } from "./core/header/advanced-search-dialog/advanced-search-dialog.component";
import { TopSearchBarComponent } from "./core/header/top-search-bar/top-search-bar.component";
import {
  VngDialogCloseDirective,
  VngSidenavComponent,
  VngIconComponent,
  VngButtonComponent,
  VngIconButtonComponent,
  VngTooltipDirective,
  VngMenuComponent,
  VngMenuTriggerDirective,
  VngCardComponent,
  VngCheckboxComponent,
  VngInputComponent,
  VngSelectComponent,
  VngDatepickerComponent,
  VngProgressBarComponent,
  VngSpinnerComponent,
  VngDialogContentComponent,
  VngDialogActionsComponent,
  VngDialogTitleDirective,
  VngDividerComponent,
  VngToggleComponent,
  VngRadioGroupComponent,
  VngRadioButtonComponent,
  VngTabsComponent,
  VngTabComponent,
  VngExpansionPanelComponent,
  VngGaugeComponent,
  VngGaugeValueDirective,
  VngGaugeLabelDirective,
  VngChipComponent,
  VngChipInputComponent,
  VngFormFieldComponent,
  VngTextareaComponent,
  VngPaginationComponent,
  VngTourAnchorDirective,
  VngCarouselComponent,
  VngModalComponent,
  VngEmptyStateComponent,
  VngBadgeComponent,
  VngSkeletonRowsComponent,
} from "@vengreso/angular-components";

import { NgbPopoverModule } from "@ng-bootstrap/ng-bootstrap";
import { BaseChartDirective, provideCharts, withDefaultRegisterables } from "ng2-charts";
import { SignOutMessageComponent } from "./components/sign-out-message/sign-out-message.component";
import { SignOutMessageService } from "./components/sign-out-message/sign-out-message.service";
import { MenuService } from "./core/menu/menu.service";
import { ProfileUpdatedSuccessfullyComponent } from "./components/dialogs/profile-updated-successfully/profile-updated-successfully.component";
import { FlyLearningComponent } from "./vengreso/flylearning/fly-learning.component";
import { BillingHistoryComponent } from "./vengreso/settings/billing-history/billing-history.component";
import { DowngradeOptionComponent } from "./vengreso/settings/downgrade-option/downgrade-option.component";
import { DowngradePlanOneComponent } from "./vengreso/settings/downgrade-plan-one/downgrade-plan-one.component";
import { DowngradePlanThreeComponent } from "./vengreso/settings/downgrade-plan-three/downgrade-plan-three.component";
import { DowngradePlanTwoComponent } from "./vengreso/settings/downgrade-plan-two/downgrade-plan-two.component";
import { DowngradedComponent } from "./vengreso/settings/downgraded/downgraded.component";
import { EditPlanComponent } from "./vengreso/settings/edit-plan/edit-plan.component";
import { FlyshareRewardsComponent } from "./vengreso/settings/flyshare-rewards/flyshare-rewards.component";
import { HistoryUsageComponent } from "./vengreso/settings/history-usage/history-usage.component";
import { NewPlanComponent } from "./vengreso/settings/new-plan/new-plan.component";
import { PausedPlanComponent } from "./vengreso/settings/paused-plan/paused-plan.component";
import { PaymentDetailsComponent } from "./vengreso/settings/payment-details/payment-details.component";
import { AddPaymentMethodComponent } from "./vengreso/settings/payment-method-details/add-payment-method/add-payment-method.component";
import { PaymentMethodDetailsComponent } from "./vengreso/settings/payment-method-details/payment-method-details.component";
import { PrimaryMethodConfirmationComponent } from "./vengreso/settings/payment-method-details/primary-method-confirmation/primary-method-confirmation.component";
import { RemovalConfirmationComponent } from "./vengreso/settings/payment-method-details/removal-confirmation/removal-confirmation.component";
import { PlanDetailsModule } from "./vengreso/settings/plans/plan-details.module";
import { AddToFlycutsComponent } from "./vengreso/share/add-to-flycuts/add-to-flycuts.component";
import { FeatureNotInPlanComponent } from "./vengreso/share/feature-not-in-plan/feature-not-in-plan.component";
import { FlyCutInviteComponent } from "./vengreso/share/fly-cut-invite/fly-cut-invite.component";
import { FlyCutNotifyComponent } from "./vengreso/share/fly-cut-notify/fly-cut-notify.component";
import { FlyCutShareComponent } from "./vengreso/share/fly-cut-share/fly-cut-share.component";
import { FlyshareComponent } from "./vengreso/share/flyshare/flyshare.component";
import { ShareHistoryComponent } from "./vengreso/share/share-history/share-history.component";
import { CookieService } from "ngx-cookie-service";
import { CustomReuseStrategy } from "./custom-reuse-strategy";
import { NgxSkeletonLoaderModule } from "ngx-skeleton-loader";
import { MaintenanceComponent } from "./maintenance/maintenance.component";
import { TestFlycutComponent } from "./vengreso/shortcuts/test-flycut/test-flycut.component";
import { GlobalOverlayComponent } from "./global-overlay/global-overlay.component";

import { YouTubePlayerModule } from "@angular/youtube-player";
import { WelcomeModalComponent } from "./dashboard/walk-me/welcome-modal/welcome-modal.component";
import { WalkMeHeaderComponent } from "./dashboard/walk-me/walk-me-header/walk-me-header.component";
import { OnboardingChecklistComponent } from "./dashboard/walk-me/onboarding-checklist/onboarding-checklist.component";
import { AboutYouStepComponent } from "./dashboard/walk-me/about-you-step/about-you-step.component";
import { AddFlycutSignatureStepComponent } from "./dashboard/walk-me/add-flycut-signature-step/add-flycut-signature-step.component";
import { BackdropComponent } from "./backdrop/backdrop.component";
import { UserPersonaComponent } from "./vengreso/user-persona/user-persona.component";
import { UserPersonaAddComponent } from "./vengreso/user-persona-add/user-persona-add.component";
import { SvgIconsModule } from "./module/svg.module";
import { LoadingComponent } from "./components/loading/loading.component";
import { ExtensionSettingsModule } from "./vengreso/settings/extension-settings/extension-settings.module";
import { MasqueradeHandlerComponent } from "./components/masquerade-handler/masquerade-handler.component";
import { CategorySelectComponent } from "./vengreso/shared/category-select/category-select.component";
import { DialogCloseButtonComponent } from "./components/dialogs/dialog-close-button/dialog-close-button.component";
import { EmptyStateComponent } from "./components/empty-state/empty-state.component";
import { CopyLinkFieldComponent } from "./components/copy-link-field/copy-link-field.component";
import { FlymsgRowHeaderComponent } from "./vengreso/shared/flymsg-row/flymsg-row-header/flymsg-row-header.component";
import { FlymsgRowEditorComponent } from "./vengreso/shared/flymsg-row/flymsg-row-editor/flymsg-row-editor.component";
import { NotificationBellComponent } from "./components/notifications/notification-bell/notification-bell.component";

const ngxUiLoaderConfig: NgxUiLoaderConfig = {
  bgsColor: "#F05A28",
  bgsPosition: POSITION.centerCenter,
  bgsSize: 80,
  bgsType: SPINNER.ballScaleMultiple, // background spinner type
  fgsColor: "#F05A28",
  fgsType: SPINNER.ballScaleMultiple, // foreground spinner type
  fgsSize: 80,
  overlayColor: "rgb(255,255,255)",
  hasProgressBar: false,
  maxTime: -1,
  minTime: 500,
};

@NgModule({
  declarations: [
    BackdropComponent,
    GlobalOverlayComponent,
    AppComponent,
    HeaderComponent,
    SidebarComponent,
    OptionsComponent,
    MenuComponent,
    AdminLayoutComponent,
    LayoutComponent,
    AuthLayoutComponent,
    SettingsComponent,
    NewPlanComponent,
    AddShortcutComponent,
    VersionHistoryComponent,
    PreviewComponent,
    StatisticsComponent,
    EmulatorPreviewComponent,
    ConfirmVersionComponent,
    DeleteVersionComponent,
    DuplicateShortcutComponent,
    CategoryDependencyComponent,
    TemplateCopyComponent,
    TemplateCategoryCopyComponent,
    ChangeShortcutCategoryComponent,
    ProfileComponent,
    UserPersonaComponent,
    UserPersonaAddComponent,
    CategoryRenameComponent,
    TopSearchBarComponent,
    AdvancedSearchDialogComponent,
    PaymentDetailsComponent,
    EditPlanComponent,
    DowngradePlanOneComponent,
    DowngradePlanTwoComponent,
    DowngradePlanThreeComponent,
    DowngradedComponent,
    EditPlanComponent,
    PausedPlanComponent,
    DowngradeOptionComponent,
    PaymentMethodDetailsComponent,
    AddPaymentMethodComponent,
    BillingHistoryComponent,
    PrimaryMethodConfirmationComponent,
    RemovalConfirmationComponent,
    HistoryUsageComponent,
    FlyLearningComponent,
    FlyCutShareComponent,
    FlyCutInviteComponent,
    FlyCutNotifyComponent,
    ProfileUpdatedSuccessfullyComponent,
    TestFlycutComponent,
    FlyshareComponent,
    FlyshareRewardsComponent,
    AddToFlycutsComponent,
    FeatureNotInPlanComponent,
    ShareHistoryComponent,
    SignOutMessageComponent,
    MaintenanceComponent,
    WelcomeModalComponent,
    WalkMeHeaderComponent,
    OnboardingChecklistComponent,
    AboutYouStepComponent,
    AddFlycutSignatureStepComponent,
    BackdropComponent,
    LoadingComponent,
    MasqueradeHandlerComponent,
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    RouterModule.forRoot(AppRoutes, { useHash: false }),
    HttpClientModule,
    MarkdownModule.forRoot(),
    BidiModule,
    CoreModule,
    NgxWebstorageModule.forRoot({
      prefix: "vengreso",
      separator: ".",
      caseSensitive: true,
    }),
    SvgIconsModule,
    NgxUiLoaderModule.forRoot(ngxUiLoaderConfig),
    EditorModule,
    DragDropModule,
    HighlighterPipe,
    ClipboardModule,
    BaseChartDirective,
    // C3-14: NgbModule/NgbTooltipModule narrowed to NgbPopoverModule — ngbPopover
    // is the only ng-bootstrap construct used anywhere in the app (menu, flyshare,
    // settings, welcome-modal, add-shortcut, duplicate-shortcut, emulator-preview,
    // test-flycut, user-persona(-add) are all declared here); ngbTooltip has zero
    // template usages repo-wide (verified).
    NgbPopoverModule,
    PlanDetailsModule,
    StoreModule.forRoot({ plan: planReducer }),
    EffectsModule.forRoot([PlanEffects]),
    NgxSkeletonLoaderModule,
    YouTubePlayerModule,
    ExtensionSettingsModule,
    // vng (@vengreso/angular-components) — ADDED for C3-1. AdvancedSearchDialogComponent's
    // "Search" button uses [vngDialogClose] (VngDialogRef has no MatDialogRef
    // DI fallback, so the old [mat-dialog-close] would throw on click).
    VngDialogCloseDirective,
    // vng — ADDED for C3-2 (app shell): admin-layout/auth-layout (vng-sidenav,
    // R18), menu (vng-icon, vng-button, vngTooltip — mini-fab CSS recipe),
    // header (vng-menu/[vngMenuTriggerFor], R10), options (vng-card,
    // vng-checkbox).
    // fix(shell): admin-layout no longer uses vng-sidenav (rebuilt on a
    // plain fixed `<aside>` — see admin-layout.component.html/.scss); kept
    // here for auth-layout.component.html, its only remaining consumer.
    VngSidenavComponent,
    VngIconComponent,
    VngButtonComponent,
    VngTooltipDirective,
    VngMenuComponent,
    VngMenuTriggerDirective,
    VngCardComponent,
    VngCheckboxComponent,
    // vng — ADDED for C3-3 (global & advanced search): top-search-bar
    // (vng-icon/vng-spinner/vng-progress-bar + R20's trivial-HTML autocomplete
    // panel — no vng-autocomplete needed), advanced-search-dialog (vng-input,
    // vng-select incl. the 04 §3.4 optgroup composition, vng-datepicker R16,
    // vng-dialog-content/-actions/-title).
    VngInputComponent,
    VngSelectComponent,
    VngDatepickerComponent,
    VngProgressBarComponent,
    VngSpinnerComponent,
    VngDialogContentComponent,
    VngDialogActionsComponent,
    VngDialogTitleDirective,
    // vng — ADDED for C3-4 (FlyCuts/shortcuts): version-history/preview
    // (vng-card, vng-input, vng-button), add-shortcut/duplicate-shortcut/
    // add-shortcut-category/change-shortcut-category (vng-select R5 incl. the
    // #vngSelectActions "Create New Category" composition, vng-divider,
    // vng-radio-group/-button R12), actions/emulator-preview (vng-toggle R13),
    // test-flycut (vng-radio-group/-button, tour anchors/OverlayContainer
    // preserved verbatim).
    VngDividerComponent,
    VngToggleComponent,
    VngRadioGroupComponent,
    VngRadioButtonComponent,
    // vng — ADDED for C3-5 (settings hub + profile): settings.component (the
    // 8-tab host — vng-tabs/vng-tab R14 with the (selectedIndexChange) rebind,
    // manage-categories plain vng-table R8, vng-card/vng-checkbox/vng-select/
    // vng-icon/vng-button already above; vng-expansion-panel R15 for the Import
    // FlyCuts accordion), profile.component (vng-input password-toggle
    // composition + vng-progress-bar password-strength bar, 04 §3.1).
    VngTabsComponent,
    VngTabComponent,
    VngExpansionPanelComponent,
    // vng — ADDED for C3-6 (billing, plans & usage): payment-details/
    // payment-method-details/add-payment-method/primary-method-confirmation/
    // removal-confirmation (vng-input/vng-select/vng-card/vng-divider/
    // vng-button chrome around the untouched Stripe Elements card mount),
    // new-plan/edit-plan/downgrade-*/downgraded/paused-plan (vng-dialog-*
    // chrome, vng-toggle, vng-radio-group/-button), billing-history (plain
    // vng-table with a CSS-only sticky thead, R8), history-usage (the
    // ngx-gauge -> vng-gauge swap, 04 §3.6 — VngGaugeValueDirective/
    // VngGaugeLabelDirective back the [vngGaugeValue]/[vngGaugeLabel]
    // projected-content slots; NgxGaugeModule/ngx-gauge removed in C6-1).
    VngGaugeComponent,
    VngGaugeValueDirective,
    VngGaugeLabelDirective,
    // vng — ADDED for C3-8 (FlyShare & share dialogs): fly-cut-invite/fly-cut-share
    // (the 04 §3.2 chip-input composition — native input + vng-chip inside
    // vng-form-field, replacing matChipInputFor/separator keycodes; vng-input,
    // vng-textarea, vng-radio-group/-button for the referral progress rows),
    // flyshare (vng-expansion-panel/.vng-accordion R15 all-multi, vng-checkbox),
    // share-history (vng-input/vng-button/vng-divider; mat-list -> plain ul,
    // no library list component), add-to-flycuts (vng-select R5 incl. the
    // the old select-search plugin -> [searchable] swap for its 3-level category
    // tree + #vngSelectActions "Create New Category" composition).
    VngChipComponent,
    // vng — ADDED for user-persona-add: vng-chip-input (a ControlValueAccessor
    // over string[]) replaces the nine hand-composed chip fields there — the
    // vng-chip *ngFor + native <input> + add/remove/onPaste/blur helpers and the
    // manual form.controls.X.setValue() sync are now the library's job.
    VngChipInputComponent,
    VngFormFieldComponent,
    VngTextareaComponent,
    // vng — ADDED for C3-10 (AI personas): user-persona (the app's only
    // MatTableDataSource+matSort+mat-paginator instance — plain
    // <table class="vng-table"> + VngTableDataSource + vng-pagination, R8,
    // 1-based page numbering; vng-select for the "Order by" control),
    // user-persona-add (vng-radio-group/-button, vng-select for Tone; its 9
    // chip fields moved off the 04 §3.2 hand-composed recipe onto
    // vng-chip-input when v19.8 shipped it — see VngChipInputComponent above).
    // ngbPopover/ngx-markdown untouched.
    VngPaginationComponent,
    // vng — ADDED for C3-15 (tour swap): VngTourAnchorDirective backs every
    // [vngTourAnchor] in AddShortcutComponent, TestFlycutComponent,
    // MenuComponent, UserPersonaComponent, UserPersonaAddComponent,
    // TemplateCopyComponent, TopSearchBarComponent (all declared here).
    // The old Material-menu tour module + its .forRoot() call are removed —
    // VngTourService is providedIn: 'root', no module wiring needed for the
    // service itself.
    VngTourAnchorDirective,
    // vng — ADDED for C3-16 (carousel swap): vng-carousel backs
    // WelcomeModalComponent's "More learning videos" screen (welcome-modal
    // declared here), replacing the prior third-party image-carousel package
    // (no Angular 19 release existed for it).
    VngCarouselComponent,
    // vng — ADDED for the sign-out modal: SignOutMessageComponent's hand-built
    // fixed backdrop + white panel + footer bar is now a vng-modal
    // (title/[vngModalBody]/[vngModalFooter] slots, esc/backdrop close).
    VngModalComponent,
    // vng — ADDED for the test-flycut modal X: the hand-rolled <button> + inline
    // 24px close SVG is now [vng-icon-button] + vng-icon (library hover/disabled
    // states); attribute selector, so the data-fms-heap hook stays put.
    VngIconButtonComponent,
    // vng — ADDED for the settings billing surfaces: vng-empty-state replaces the
    // duplicated `.blur` "No Invoices" / "Payment method not available" blocks
    // (billing-history, payment-method-details), and vng-badge replaces the bare
    // <button class="green|red"> invoice status pill in billing-history (which was
    // a focusable, clickable button for a non-interactive status).
    VngEmptyStateComponent,
    VngBadgeComponent,
    // Shared standalone components (dedup sweep):
    // - app-category-select: add-shortcut, duplicate-shortcut,
    //   change-shortcut-category, template-copy, template-category-copy,
    //   add-to-flycuts (all declared here).
    // - app-dialog-close-button: settings plan/payment dialogs, version-history,
    //   fly-cut-share/-invite/-notify, share-history (all declared here;
    //   warning-dialog and the onboarding dialogs import it via their modules).
    // - app-empty-state: flyshare (dashboard/shortcuts import it themselves).
    // - app-copy-link-field: fly-cut-share, fly-cut-invite.
    // - app-flymsg-row-header / app-flymsg-row-editor: settings' Import FlyCuts
    //   preview rows (SettingsComponent is declared here; FlyCuts, FlyPlates and
    //   the dashboard widget are standalone/lazy and import them themselves).
    CategorySelectComponent,
    DialogCloseButtonComponent,
    EmptyStateComponent,
    CopyLinkFieldComponent,
    FlymsgRowHeaderComponent,
    FlymsgRowEditorComponent,
    // - app-notification-bell: HeaderComponent (declared here) mounts it
    //   *ngIf="showNotificationCenter" (issue #416's is_beta gate).
    NotificationBellComponent,
    // vng — ADDED for issue #538: AboutYouStepComponent (declared here) shows
    // vng-skeleton-rows while GET v1/user/onboarding/questions is in flight,
    // matching the repo's existing loading pattern (notification-panel).
    VngSkeletonRowsComponent,
  ],
  providers: [
    provideCharts(withDefaultRegisterables()),
    TemplatesResolver,
    ShortcutCategoryListResolver,
    ShortcutListResolver,
    TemplatesCategoryListResolver,
    SettingsResolver,
    ProfileResolver,
    AuthGuardService,
    MenuService,
    GuestGuardService,
    SignOutMessageService,
    {
      provide: "SocialAuthServiceConfig",
      useValue: {
        autoLogin: false,
        providers: [
          {
            id: GoogleLoginProvider.PROVIDER_ID,
            provider: new GoogleLoginProvider(environment.GOOGLE_CLIENT_ID, {
              oneTapEnabled: false, // default is true
            }),
          },
        ],
        onError: (err) => {},
      } as SocialAuthServiceConfig,
    },
    CookieService,
    { provide: RouteReuseStrategy, useClass: CustomReuseStrategy },
  ],
  bootstrap: [AppComponent],
  exports: [SvgIconsModule],
})
export class AppModule {}
